// Is the Founding Member offer still being SOLD? Issue #443.
//
// Pro.tsx used to hardcode `eyebrow="FOUNDING PRICING ENDS AUG 31"` above a live
// $79 buy button, with no date logic behind it. On Sep 1 2026 that becomes a
// false claim printed directly above a purchase button — the card would go on
// selling founding pricing while announcing that the deadline for it had
// passed. The Jul 27 decision was that founding pricing genuinely expires:
// after Aug 31 the card goes away, it is not merely relabelled.
//
// ⚠️ This is about whether the card is still SOLD. It says nothing about
// honouring what was already sold — a mom who bought Founding keeps her 12
// months, and none of that logic lives here or is touched by this.
//
// ── On the timezone ────────────────────────────────────────────────────────
//
// Evaluated in America/New_York, the same zone the rest of the app treats as
// "her day" (see daily-digest/index.ts). Deliberately ONE inference point:
// this decision once got made separately in five places and drifted, and
// unpicking that took four issues to close. Don't add a sixth — import this.
//
// (This used to name those four as "all open". They closed Aug 12-13 2026,
// and the stale claim was still being quoted into new PRs three weeks later
// as live evidence — #1262 did exactly that. The rule is what matters here;
// the issue numbers were doing no work that outlived their closure.)
//
// Intl does the DST arithmetic, so this stays correct across the Nov 1 2026
// fallback without a table of offsets. The "en-CA" locale is not decorative:
// it formats as YYYY-MM-DD, which makes the comparison below a plain
// lexicographic string compare on two ISO dates. No Date parsing, no UTC
// round-trip, nothing to get wrong at a midnight boundary.

/**
 * Last day the Founding Member card is offered, inclusive, in ET.
 *
 * ⚠️ This constant is the load-bearing one, and everything above describing
 * the offer as expiring "after Aug 31" is a restatement of it, not an
 * independent fact. Change it — a second founding window, or an extension —
 * and re-read this whole header, plus the absence assertion in
 * e2e/pro-billing.spec.ts that goes falsely red the moment the tile returns.
 */
export const FOUNDING_LAST_DAY_ET = "2026-08-31";

/** Today's date in America/New_York as "YYYY-MM-DD". */
export const etDateString = (now: Date): string =>
  new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/New_York",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(now);

/**
 * True while the Founding Member card should still render. Inclusive of the
 * last day — on Aug 31 in ET the offer is still open; it closes at the first
 * moment of Sep 1 ET.
 */
export const isFoundingOfferOpen = (now: Date = new Date()): boolean =>
  etDateString(now) <= FOUNDING_LAST_DAY_ET;
